import dayjs from 'dayjs';

import { ESubscriptionStatus, TSubscription } from '@mopla/data-models';

import { ECancellationCase } from '../types';

export const getCancellationCase = (
	subscription: TSubscription
): ECancellationCase | null => {
	if (!subscription) return null;

	const today = dayjs();

	const isPaused = subscription.status === ESubscriptionStatus.PAUSED;
	const isPreordered = subscription.status === ESubscriptionStatus.PREORDERED;
	const hasPendingPause = !!(
		subscription.pauseStart &&
		subscription.pauseEnd &&
		!isPaused
	);
	const hasPendingCancel = !!subscription.canceledAt;
	// For active tickets: cancellationNoticeDeadline in the current month → in time.
	// If the deadline is in a future month → we missed this month's cutoff → out of time.
	const isAfterCancellationDeadline = subscription.cancellationNoticeDeadline
		? !dayjs(subscription.cancellationNoticeDeadline).isSame(today, 'month')
		: today.date() > 10;

	// For paused tickets: the deadline may be in a future month but still hasn't passed.
	// Use isAfter to check if today is actually past the deadline.
	const isPausedAndAfterDeadline = subscription.cancellationNoticeDeadline
		? today.isAfter(dayjs(subscription.cancellationNoticeDeadline))
		: today.date() > 10;

	// Order matters: check specific cases first
	if (hasPendingCancel) return ECancellationCase.Revoke;
	if (isPaused && isPausedAndAfterDeadline)
		return ECancellationCase.ActivePauseCancelOutTime;
	if (isPaused) return ECancellationCase.ActivePause;
	if (hasPendingPause) return ECancellationCase.PendingPause;
	// For preordered: less than 24h before subscription start → out of time
	if (isPreordered) {
		const subscriptionStart = dayjs(subscription.subscriptionStart);
		const isLessThan24hBeforeStart = subscriptionStart.diff(today, 'hour') < 24;
		return isLessThan24hBeforeStart
			? ECancellationCase.PreorderCancelOutTime
			: ECancellationCase.PreorderCancelInTime;
	}
	if (isAfterCancellationDeadline) return ECancellationCase.CancelOutTime;

	return ECancellationCase.CancelInTime;
};
